import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldAlert,
  ShieldCheck,
  Activity,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Loader2,
  Search,
  PlayCircle,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  auditAPI,
  AlertFilters,
  AlertSeverity,
  AlertStatistics,
  AlertStatus,
  SecurityAlertListItem,
  SecurityAuditLog,
  RateLimitViolation,
} from '@/services/audit-api';

type Tab = 'alerts' | 'audit' | 'rate';

const RANGES: Array<{ key: '24h' | '7d' | '30d'; label: string; hours: number }> = [
  { key: '24h', label: '24H', hours: 24 },
  { key: '7d', label: '7D', hours: 168 },
  { key: '30d', label: '30D', hours: 720 },
];

const SEVERITY_TONE: Record<AlertSeverity, string> = {
  critical: 'bg-[var(--lib-danger)]/10 text-[var(--lib-danger)] border-[var(--lib-danger)]/30',
  high: 'bg-[var(--lib-gold)]/10 text-[var(--lib-gold-text)] border-[var(--lib-gold)]/30',
  medium: 'bg-[var(--lib-gold)]/10 text-[var(--lib-gold-text)] border-[var(--lib-gold)]/30',
  low: 'bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)] border-[var(--lib-hairline)]',
};

const STATUS_TONE: Record<AlertStatus, string> = {
  open: 'bg-[var(--lib-danger)]/10 text-[var(--lib-danger)] border-[var(--lib-danger)]/30',
  acknowledged: 'bg-[var(--lib-navy)]/10 text-[var(--lib-navy)] border-[var(--lib-navy)]/30',
  investigating: 'bg-[var(--lib-gold)]/10 text-[var(--lib-gold)] border-[var(--lib-gold)]/30',
  resolved: 'bg-[var(--lib-success)]/10 text-[var(--lib-success)] border-[var(--lib-success)]/30',
  false_positive: 'bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)] border-[var(--lib-hairline)]',
};

const SEVERITY_OPTIONS: AlertSeverity[] = ['critical', 'high', 'medium', 'low'];
const STATUS_OPTIONS: AlertStatus[] = ['open', 'acknowledged', 'investigating', 'resolved', 'false_positive'];

const fmtDate = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Kicker: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">{children}</div>
);

const SecurityModule: React.FC = () => {
  const [tab, setTab] = useState<Tab>('alerts');
  const [rangeKey, setRangeKey] = useState<'24h' | '7d' | '30d'>('24h');
  const range = useMemo(() => RANGES.find((r) => r.key === rangeKey)!, [rangeKey]);

  const [stats, setStats] = useState<AlertStatistics | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [alerts, setAlerts] = useState<SecurityAlertListItem[]>([]);
  const [auditLogs, setAuditLogs] = useState<SecurityAuditLog[]>([]);
  const [violations, setViolations] = useState<RateLimitViolation[]>([]);
  const [tabLoading, setTabLoading] = useState(false);

  const [severityFilter, setSeverityFilter] = useState<AlertSeverity | ''>('');
  const [statusFilter, setStatusFilter] = useState<AlertStatus | ''>('');
  const [search, setSearch] = useState('');
  const [runningMonitor, setRunningMonitor] = useState(false);

  // Stats: refetch on range change
  useEffect(() => {
    let cancel = false;
    setStatsLoading(true);
    auditAPI
      .alertStatistics(range.hours)
      .then((s) => {
        if (!cancel) setStats(s);
      })
      .catch(() => {
        if (!cancel) setStats(null);
      })
      .finally(() => {
        if (!cancel) setStatsLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [range.hours]);

  // Tab data: refetch on tab/range/filter change
  useEffect(() => {
    let cancel = false;
    setTabLoading(true);

    const startDate = new Date(Date.now() - range.hours * 3600 * 1000).toISOString();

    const fetcher = async () => {
      if (tab === 'alerts') {
        const filters: AlertFilters = { start_date: startDate, page_size: 50 };
        if (severityFilter) filters.severity = severityFilter;
        if (statusFilter) filters.status = statusFilter;
        const data = await auditAPI.listAlerts(filters);
        if (!cancel) setAlerts(data.results);
      } else if (tab === 'audit') {
        const data = await auditAPI.listAuditLogs({
          start_date: startDate,
          page_size: 50,
          ...(severityFilter ? { severity: severityFilter } : {}),
          ...(search.trim() ? { username: search.trim() } : {}),
        });
        if (!cancel) setAuditLogs(data.results);
      } else {
        const data = await auditAPI.listViolations({
          start_date: startDate,
          page_size: 50,
          ...(search.trim() ? { ip_address: search.trim() } : {}),
        });
        if (!cancel) setViolations(data.results);
      }
    };

    fetcher()
      .catch(() => {
        if (!cancel) {
          setAlerts([]);
          setAuditLogs([]);
          setViolations([]);
        }
      })
      .finally(() => {
        if (!cancel) setTabLoading(false);
      });

    return () => {
      cancel = true;
    };
  }, [tab, range.hours, severityFilter, statusFilter, search]);

  const handleResolve = async (id: number) => {
    try {
      await auditAPI.resolveAlert(id, 'Resolved by admin');
      setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, status: 'resolved', status_display: 'Resolved' } : a)));
      toast.success('Alert resolved');
    } catch {
      // toast already raised by interceptor
    }
  };

  const handleRunMonitor = async () => {
    setRunningMonitor(true);
    try {
      const summary = await auditAPI.runMonitoring();
      const created = (summary as any)?.alerts_created ?? (summary as any)?.created ?? 0;
      toast.success(`Monitoring cycle complete · ${created} new alert${created === 1 ? '' : 's'}`);
      // Refresh stats + alerts
      const s = await auditAPI.alertStatistics(range.hours);
      setStats(s);
      if (tab === 'alerts') {
        const startDate = new Date(Date.now() - range.hours * 3600 * 1000).toISOString();
        const data = await auditAPI.listAlerts({ start_date: startDate, page_size: 50 });
        setAlerts(data.results);
      }
    } catch {
      // interceptor toasts
    } finally {
      setRunningMonitor(false);
    }
  };

  const kpis = useMemo(() => {
    if (!stats) {
      return [
        { label: 'Alerts', value: '—' },
        { label: 'Critical', value: '—' },
        { label: 'Open', value: '—' },
        { label: 'Resolved', value: '—' },
      ];
    }
    return [
      { label: 'Alerts', value: (stats.total ?? 0).toLocaleString() },
      { label: 'Critical', value: String(stats.by_severity?.critical ?? 0) },
      { label: 'Open', value: String(stats.open_count ?? stats.by_status?.open ?? 0) },
      { label: 'Resolved', value: String(stats.resolved_count ?? stats.by_status?.resolved ?? 0) },
    ];
  }, [stats]);

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)] flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-[var(--lib-gold)]" />
            Security
          </h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">
            Audit logs, security alerts and rate-limit violations.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r.key}
                onClick={() => setRangeKey(r.key)}
                className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${
                  rangeKey === r.key ? 'bg-[var(--lib-gold)] text-[var(--lib-on-gold)]' : 'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)]'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleRunMonitor}
            disabled={runningMonitor}
            className="border border-[var(--lib-gold-text)] text-[var(--lib-gold-text)] hover:bg-[var(--lib-surface-2)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {runningMonitor ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Run monitor
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[var(--lib-hairline)]">
          {kpis.map((kpi) => (
            <div key={kpi.label} className="px-6 py-4">
              <div className="font-display text-[28px] font-bold text-[var(--lib-ink)]">{statsLoading ? '…' : kpi.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mt-1">
                {kpi.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--lib-hairline)]">
        {([
          { key: 'alerts', label: 'Alerts', icon: AlertTriangle },
          { key: 'audit', label: 'Audit log', icon: Activity },
          { key: 'rate', label: 'Rate limits', icon: Ban },
        ] as const).map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => {
                setTab(t.key);
                setSearch('');
                setSeverityFilter('');
                setStatusFilter('');
              }}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium relative transition-colors ${
                active ? 'text-[var(--lib-gold-text)]' : 'text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)]'
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {active && <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-[var(--lib-gold)]" />}
            </button>
          );
        })}
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3">
        {(tab === 'alerts' || tab === 'audit') && (
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as AlertSeverity | '')}
            className="text-[13px] h-9 px-3 rounded-md bg-transparent border border-[var(--lib-hairline)] text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)]"
          >
            <option value="">All severities</option>
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        )}
        {tab === 'alerts' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AlertStatus | '')}
            className="text-[13px] h-9 px-3 rounded-md bg-transparent border border-[var(--lib-hairline)] text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)]"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
        )}
        {(tab === 'audit' || tab === 'rate') && (
          <div className="relative flex-1 min-w-[220px] max-w-[360px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--lib-ink-muted)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === 'audit' ? 'Filter by username…' : 'Filter by IP address…'}
              className="w-full h-9 pl-9 pr-3 rounded-md bg-transparent border border-[var(--lib-hairline)] text-[13px] text-[var(--lib-ink)] focus:outline-none focus:border-[var(--lib-gold)]"
            />
          </div>
        )}
        <button
          onClick={() => {
            setRangeKey(rangeKey);
          }}
          className="ml-auto font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] hover:text-[var(--lib-ink)] transition-colors flex items-center gap-1.5"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Tab content */}
      <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg overflow-hidden">
        {tabLoading ? (
          <div className="py-16 flex items-center justify-center text-[var(--lib-ink-muted)] text-sm">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading…
          </div>
        ) : tab === 'alerts' ? (
          <AlertsTable alerts={alerts} onResolve={handleResolve} />
        ) : tab === 'audit' ? (
          <AuditTable logs={auditLogs} />
        ) : (
          <ViolationsTable violations={violations} />
        )}
      </div>
    </div>
  );
};

/* ─────── Sub-tables ─────── */

const AlertsTable: React.FC<{
  alerts: SecurityAlertListItem[];
  onResolve: (id: number) => void;
}> = ({ alerts, onResolve }) => {
  if (alerts.length === 0) {
    return (
      <div className="py-16 flex flex-col items-center justify-center text-[var(--lib-ink-muted)] text-sm gap-2">
        <ShieldCheck className="h-8 w-8 text-[var(--lib-success)]" />
        No security alerts in this window.
      </div>
    );
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)]">
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Created</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Severity</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Title</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">IP</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Status</th>
          <th className="text-right text-xs font-mono uppercase tracking-wider px-5 py-3">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--lib-hairline)]">
        {alerts.map((a) => (
          <tr key={a.id} className="hover:bg-[var(--lib-surface-2)] transition-colors">
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)] whitespace-nowrap">
              {fmtDate(a.created_at)}
            </td>
            <td className="px-5 py-3">
              <span
                className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${SEVERITY_TONE[a.severity]}`}
              >
                {a.severity_display ?? a.severity}
              </span>
            </td>
            <td className="px-5 py-3 text-[var(--lib-ink)]">
              <div className="font-medium">{a.title}</div>
              <div className="text-xs text-[var(--lib-ink-muted)]">{a.alert_type_display}</div>
            </td>
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)]">{a.ip_address ?? '—'}</td>
            <td className="px-5 py-3">
              <span
                className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${STATUS_TONE[a.status]}`}
              >
                {a.status_display ?? a.status}
              </span>
            </td>
            <td className="px-5 py-3 text-right">
              {a.status !== 'resolved' && a.status !== 'false_positive' ? (
                <button
                  onClick={() => onResolve(a.id)}
                  className="inline-flex items-center gap-1 text-xs text-[var(--lib-success)] hover:text-[var(--lib-success)] font-medium"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Resolve
                </button>
              ) : (
                <span className="text-xs text-[var(--lib-ink-muted)]">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const AuditTable: React.FC<{ logs: SecurityAuditLog[] }> = ({ logs }) => {
  if (logs.length === 0) {
    return <div className="py-16 text-center text-sm text-[var(--lib-ink-muted)]">No audit log entries.</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)]">
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Time</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Event</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">User</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">IP</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Path</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Severity</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--lib-hairline)]">
        {logs.map((log) => (
          <tr key={log.id} className="hover:bg-[var(--lib-surface-2)] transition-colors">
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)] whitespace-nowrap">
              {fmtDate(log.timestamp)}
            </td>
            <td className="px-5 py-3 text-[var(--lib-ink)]">{log.event_type_display ?? log.event_type}</td>
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)]">{log.username ?? 'anonymous'}</td>
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)]">{log.ip_address ?? '—'}</td>
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)] truncate max-w-[280px]">
              <span className="text-[var(--lib-ink-muted)] mr-1.5">{log.request_method}</span>
              {log.request_path}
            </td>
            <td className="px-5 py-3">
              <span
                className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border ${SEVERITY_TONE[log.severity]}`}
              >
                {log.severity_display ?? log.severity}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ViolationsTable: React.FC<{ violations: RateLimitViolation[] }> = ({ violations }) => {
  if (violations.length === 0) {
    return <div className="py-16 text-center text-sm text-[var(--lib-ink-muted)]">No rate-limit violations.</div>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)]">
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Time</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">IP</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">User</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Endpoint</th>
          <th className="text-right text-xs font-mono uppercase tracking-wider px-5 py-3">Count</th>
          <th className="text-left text-xs font-mono uppercase tracking-wider px-5 py-3">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--lib-hairline)]">
        {violations.map((v) => (
          <tr key={v.id} className="hover:bg-[var(--lib-surface-2)] transition-colors">
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)] whitespace-nowrap">
              {fmtDate(v.timestamp)}
            </td>
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink)]">{v.ip_address}</td>
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)]">{v.user_username ?? 'anonymous'}</td>
            <td className="px-5 py-3 font-mono text-xs text-[var(--lib-ink-muted)] truncate max-w-[280px]">
              <span className="text-[var(--lib-ink-muted)] mr-1.5">{v.method}</span>
              {v.endpoint}
            </td>
            <td className="px-5 py-3 text-right font-mono text-xs text-[var(--lib-ink)]">{v.violation_count}</td>
            <td className="px-5 py-3">
              {v.is_currently_blocked ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border bg-[var(--lib-danger)]/10 text-[var(--lib-danger)] border-[var(--lib-danger)]/30">
                  <Ban className="h-3 w-3" />
                  Blocked
                </span>
              ) : (
                <span className="inline-flex px-2 py-0.5 text-[11px] font-medium rounded-full border bg-[var(--lib-surface-2)] text-[var(--lib-ink-muted)] border-[var(--lib-hairline)]">
                  Recorded
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default SecurityModule;
